// frontend/src/pages/dashboard/ProjectComponents/ProjectDetailsDialog.js
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Tooltip,
  Button,
  Chip
} from "@mui/material";
import {
  Close as CloseIcon,
  Timeline as TimelineIcon,
  Flag as FlagIcon,
  Dashboard as DashboardIcon,
  Task as TaskIcon,
  TrendingDown as TrendingDownIcon,
  Download as DownloadIcon
} from "@mui/icons-material";
import BurndownChart from '../../../components/charts/BurndownChart';
import { getAvatarUrl } from "../../../utils/imageHelpers";

const ProjectDetailsDialog = ({
  open,
  onClose,
  viewingProject,
  attachments,
  projectFinancials,
  burndownData,
  burndownLoading,
  isAdmin,
  company,
  handleDownload,
  navigate
}) => {
  const formatDate = (dateString, format = "MMM DD") => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const options = { month: 'short', day: '2-digit' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatBillingInfo = (project) => {
    if (!project) return "N/A";
    const currency = project.currency || "USD";
    switch (project.billing_model) {
      case "hourly": return `${project.hourly_rate || "0"} ${currency}/hr`;
      case "fixed": return `${project.fixed_price || "0"} ${currency} (Fixed)`;
      case "monthly": return `${project.fixed_price || "0"} ${currency}/month`;
      case "quarter": return `${project.fixed_price || "0"} ${currency}/quarter`;
      case "half-yearly": return `${project.fixed_price || "0"} ${currency}/6 months`;
      case "yearly": return `${project.fixed_price || "0"} ${currency}/year`;
      case "mixed": return `Mixed (${project.hourly_rate || "0"}/hr + ${project.fixed_price || "0"} fixed)`;
      case "not-applicable": default: return "Not Applicable";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight={600}>Project Details</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {viewingProject && (<>
          <Typography variant="h5" fontWeight={700} gutterBottom>{viewingProject.name || "N/A"}</Typography>
          <Chip label={(viewingProject.status?.name || "Unknown").toUpperCase()} sx={{ backgroundColor: viewingProject.status?.color || "#9CA3AF", color: "#fff", fontWeight: 600, mb: 2 }} size="small" />
        </>)}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>DESCRIPTION</Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}><Typography variant="body2">{viewingProject?.description || "No description provided"}</Typography></Paper>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>ASSIGNED TEAM</Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
            {viewingProject?.assignedUsers?.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {viewingProject.assignedUsers.map((user) => (
                  <Tooltip key={user.id} title={`${user.name} — ${user.role}`}><Avatar sx={{ width: 32, height: 32, mr: 0.5 }} src={getAvatarUrl(user.avatar)} onError={(e) => { e.target.onerror = null; e.target.src = null; }}>{!user.avatar ? user.name.charAt(0) : null}</Avatar></Tooltip>
                ))}
              </Box>
            ) : (<Typography variant="body2" color="text.secondary">No team members assigned.</Typography>)}
          </Paper>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>CLIENT</Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}><Typography variant="body2">{viewingProject?.client?.name || "Not assigned"}</Typography></Paper>
        </Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item size={{ xs: 12, sm: 6 }}><Typography variant="subtitle2" color="text.secondary" gutterBottom>START DATE</Typography><Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}><Typography variant="body2">{viewingProject?.start_date ? formatDate(viewingProject.start_date) : "N/A"}</Typography></Paper></Grid>
          <Grid item size={{ xs: 12, sm: 6 }}><Typography variant="subtitle2" color="text.secondary" gutterBottom>END DATE</Typography><Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}><Typography variant="body2">{viewingProject?.end_date ? formatDate(viewingProject.end_date) : "N/A"}</Typography></Paper></Grid>
          <Grid item size={{ xs: 12, sm: 6 }}><Typography variant="subtitle2" color="text.secondary" gutterBottom>ESTIMATED HOURS</Typography><Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}><Typography variant="body2">{viewingProject?.estimated_hours || "—"}</Typography></Paper></Grid>
          <Grid item size={{ xs: 12, sm: 6 }}><Typography variant="subtitle2" color="text.secondary" gutterBottom>BILLING</Typography><Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}><Typography variant="body2">{formatBillingInfo(viewingProject)}</Typography></Paper></Grid>
        </Grid>
        {isAdmin && viewingProject && viewingProject.financials && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, border: '1px solid #ddd' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#1976d2' }}>💰 Project Financials (Admin Only)</Typography>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 4 }}><Box sx={{ textAlign: 'center' }}><Typography variant="caption" color="text.secondary">Total Billing</Typography><Typography variant="h6" color="success.main" fontWeight="bold">{viewingProject?.currency || 'USD'} {viewingProject?.financials?.total_billing?.toFixed(2) || '0.00'}</Typography></Box></Grid>
              <Grid item size={{ xs: 12, sm: 4 }}><Box sx={{ textAlign: 'center' }}><Typography variant="caption" color="text.secondary">Total Cost (Salary)</Typography><Typography variant="h6" color="error.main" fontWeight="bold">{viewingProject?.currency || 'USD'} {viewingProject?.financials?.total_cost?.toFixed(2) || '0.00'}</Typography></Box></Grid>
              <Grid item size={{ xs: 12, sm: 4 }}><Box sx={{ textAlign: 'center' }}><Typography variant="caption" color="text.secondary">Net Profit</Typography><Typography variant="h6" color={(viewingProject?.financials?.profit ?? 0) >= 0 ? 'primary.main' : 'error.main'} fontWeight="bold">{viewingProject?.currency || 'USD'} {(viewingProject?.financials?.profit ?? 0).toFixed(2)}</Typography></Box></Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>* Cost calculated based on team salaries and tracked hours.</Typography>
          </Box>
        )}
        <Divider sx={{ my: 3 }} />
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><TrendingDownIcon sx={{ color: 'primary.main' }} />Progress Tracking</Typography>
          <BurndownChart data={burndownData} loading={burndownLoading} project={viewingProject} height={400} />
        </Box>
        <Box sx={{ my: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>ATTACHMENTS</Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
            {attachments.length > 0 ? (
              <List dense>
                {attachments.map((att) => (
                  <ListItem key={att.id} secondaryAction={<IconButton edge="end" onClick={() => handleDownload(att)} color="primary"><DownloadIcon /></IconButton>} sx={{ pl: 0 }}>
                    <ListItemText primary={att.original_name} secondary={`${(att.size / 1024).toFixed(1)} KB`} />
                  </ListItem>
                ))}
              </List>
            ) : (<Typography variant="body2" color="text.secondary">No attachments.</Typography>)}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button onClick={onClose} color="inherit" sx={{ px: 2.5, py: 1.2, borderRadius: 3, fontWeight: 600, textTransform: 'none' }}>Close</Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" startIcon={<TimelineIcon />} onClick={() => navigate(`/projects/${viewingProject?.id}/backlog`)} sx={{ px: 2.5, py: 1.2, borderRadius: 3, fontWeight: 600, textTransform: 'none' }}>Backlog</Button>
          <Button variant="outlined" startIcon={<FlagIcon />} onClick={() => navigate(`/projects/${viewingProject?.id}/sprints`)} sx={{ px: 2.5, py: 1.2, borderRadius: 3, fontWeight: 600, textTransform: 'none' }}>Sprints</Button>
          <Button variant="outlined" startIcon={<DashboardIcon />} onClick={() => navigate(`/projects/${viewingProject?.id}/board`)} sx={{ px: 2.5, py: 1.2, borderRadius: 3, fontWeight: 600, textTransform: 'none' }}>Board</Button>
          <Button variant="contained" startIcon={<TaskIcon />} onClick={() => navigate(`/tasks?project=${viewingProject?.id}`)} sx={{ textTransform: "none", fontWeight: 600, px: 3 }}>Tasks</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectDetailsDialog;